import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { apiError } from "../../shared/api/client";
import { meApi } from "../../shared/api/me";
import {
  websiteApi,
  type AddyTheme,
  type AddyThemeTokens,
  type GatewaySiteSync,
  type OtpChannelCheck,
  type Website,
} from "../../shared/api/website";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  SLUG_PREFIX,
  previewColors,
  slugPlaceholder,
  type PreviewSpec,
} from "./websitePreviewRules";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { copyText } from "../../shared/clipboard";
import {
  RESULTS_LOOKUP_OFF_WARNING,
  ResultsLookupStatusBadge,
  resultsLookupReasonText,
  turnOnBlockers,
} from "./ResultsLookupStatus";

/**
 * Website module (ADR-0040). The clinic's public website is powered by Addy Sites; this page is the
 * clinic-facing control surface and deliberately hides the technical wiring (tenant id, gateway API
 * key, connect/disconnect). Addy Sites is auto-connected behind the scenes; the clinic only picks a
 * public address (slug) and a theme. Programmatic API keys — for a clinic building its own app —
 * belong on a separate developer/integrations surface, not here.
 */

const GOALS = [
  { id: "booking", label: "Tăng đặt lịch" },
  { id: "clinic", label: "Giới thiệu phòng khám" },
  { id: "doctors", label: "Giới thiệu bác sĩ" },
  { id: "services", label: "Giới thiệu dịch vụ" },
  { id: "brand", label: "Xây dựng thương hiệu" },
];

export function WebsiteDashboardPage() {
  const qc = useQueryClient();
  const site = useQuery({ queryKey: ["website"], queryFn: websiteApi.get });
  // ADR-0040: the theme catalog lives in addy-sites; HIS picks from the same 4 themes.
  const themes = useQuery({
    queryKey: ["website", "available-themes"],
    queryFn: websiteApi.availableThemes,
  });

  const [step, setStep] = useState<1 | 2>(1);
  // Bước 1 opens on the theme that suits the clinic's vertical rather than on nothing: the clinic
  // already answered "what kind of clinic are you", and asking again with a blank picker throws
  // that answer away. `picked` stays null until the clinic actually chooses, so the pre-selection
  // appears the moment the themes arrive without fighting a later click.
  const [picked, setPicked] = useState<string | null>(null);
  const recommendedKey = themes.data?.recommendedKey ?? "";
  const recommendedReason = themes.data?.recommendedReason ?? "";
  const themeId = picked ?? recommendedKey;
  const [goals, setGoals] = useState<string[]>(["booking"]);

  const create = useMutation({
    mutationFn: () => websiteApi.create(themeId, goals),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["website"] }),
  });

  // "Quản lý nội dung website": HIS mints a one-time SSO token and opens the Addy Sites CMS as Owner
  // in a NEW TAB — no second login (ADR-0016 addendum), and HIS stays open so the clinic can flip back.
  // The CMS sets its own auth cookie on its origin from the token. 503 = SSO not configured for this env.
  //
  // The tab is opened SYNCHRONOUSLY on click (see the button below) and only navigated once the
  // token arrives. Opening it in onSuccess instead — i.e. after the network round trip — gets the
  // popup blocked: browsers only honour window.open while the user-gesture token is still live, and
  // awaiting a request consumes it. The clinic then saw nothing happen, went to the CMS by hand, and
  // landed on its login form, which is exactly the SSO-looks-broken report.
  const openCms = useMutation({ mutationFn: websiteApi.ssoLink });

  function openCmsTab() {
    // `noopener` cannot be passed here: with it window.open returns null and there is no handle to
    // navigate. Opening bare and clearing `opener` right after gives the same protection.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    openCms.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (tab && !tab.closed) {
          tab.location.replace(url);
        } else {
          // Popup blocked or the tab was closed while waiting — go in this tab rather than leave the
          // click doing nothing. The token is short-lived, so silently dropping it is a dead end.
          window.location.href = url;
        }
      },
      onError: () => tab?.close(),
    });
  }

  if (site.isLoading)
    return (
      <div className="mx-auto max-w-5xl">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-6 h-40 w-full" />
      </div>
    );
  if (site.isError)
    return (
      <div className="mx-auto max-w-5xl">
        <p className="text-sm text-destructive">
          Không tải được website: {apiError(site.error).detail}
        </p>
      </div>
    );

  // ── Has a website ───────────────────────────────────────────────────────
  if (site.data) {
    const w = site.data;
    return (
      <div className="mx-auto max-w-5xl">
        <PageHeader
          title="Website"
          description="Website công khai của phòng khám. Chỉnh đường dẫn và giao diện tại đây; thông tin phòng khám, bác sĩ và dịch vụ được lấy sẵn từ Addy HIS."
        />

        {/* 0 · Quản lý nội dung (mở Addy Sites CMS bằng SSO — không cần đăng nhập lần 2) */}
        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Quản lý nội dung website</h2>
              <p className="mt-1 max-w-prose text-sm text-muted-foreground">
                Mở trình soạn nội dung ở <b>Addy Sites</b> — trang chủ, dịch vụ, bác sĩ, bài viết và
                giao diện chi tiết — tại <b>tab mới</b>, đăng nhập tự động, Addy HIS vẫn mở để bạn
                quay lại.
              </p>
              <p className="mt-2 max-w-prose text-sm text-muted-foreground">
                Cùng một nội dung đó cũng chạy trên Zalo Mini App. Phần riêng của Mini App (phát
                hành, ẩn/hiện theo kênh) nằm ở mục <b>Zalo Mini App</b> trong trình soạn, mở nhanh
                từ trang Zalo Mini App bên này.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* #18748 — the two things an owner actually wants from this page, side by side:
                  see the site a patient sees, and edit it. "Xem website" was only ever reachable
                  by noticing the URL further down the page was a link. */}
              {w.publicUrl && (
                <Button variant="secondary" asChild>
                  <a href={w.publicUrl} target="_blank" rel="noreferrer">
                    Xem website ↗
                  </a>
                </Button>
              )}
              <Button onClick={openCmsTab} disabled={openCms.isPending}>
                {openCms.isPending ? "Đang mở…" : "Quản lý nội dung ↗"}
              </Button>
            </div>
          </CardContent>
        </Card>
        {openCms.isError && (
          <p className="mb-4 text-sm text-destructive">{apiError(openCms.error).detail}</p>
        )}

        {/* 1 · Đường dẫn công khai (slug) */}
        <Card className="mb-4">
          <CardContent className="p-5">
            <SlugCustomizer website={w} />
          </CardContent>
        </Card>

        {/* 2 · Giao diện */}
        <Card className="mb-4">
          <CardContent className="p-5">
            <ThemeHandoff />
          </CardContent>
        </Card>

        {/* 3 · Kết nối đặt lịch (#19003) — trạng thái, không bao giờ hiện khoá */}
        <Card className="mb-4">
          <CardContent className="p-5">
            <BookingConnection />
          </CardContent>
        </Card>

        {/* 4 · Tra cứu kết quả khám (#19138) — HIS cho phép, Addy Sites quyết định hiện */}
        <Card>
          <CardContent className="p-5">
            <ResultsLookupSetting />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── No website yet: onboarding ──────────────────────────────────────────
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Website"
        description="Tạo website chuyên nghiệp cho phòng khám. Thông tin phòng khám, dịch vụ và bác sĩ được lấy sẵn từ Addy HIS — không phải nhập lại."
      />

      {step === 1 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-foreground">Bước 1 · Chọn giao diện</h2>
            <div className="mt-4 grid grid-cols-2 gap-3.5 md:grid-cols-3">
              {(themes.data?.themes ?? []).map((t) => (
                <ThemeCard
                  key={t.key}
                  theme={t}
                  selected={t.key === themeId}
                  recommendedFor={t.key === recommendedKey ? recommendedReason : ""}
                  onSelect={() => setPicked(t.key)}
                />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <Button disabled={!themeId} onClick={() => setStep(2)}>
                Tiếp tục
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-foreground">Bước 2 · Mục tiêu của website</h2>
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
              Mục tiêu quyết định thứ tự các phần trên trang chủ. Có thể đổi lại sau.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {GOALS.map((g) => {
                const checked = goals.includes(g.id);
                return (
                  <label
                    key={g.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md border border-input px-3.5 py-2.5 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        setGoals((prev) =>
                          v === true ? [...prev, g.id] : prev.filter((x) => x !== g.id),
                        )
                      }
                    />
                    <span>{g.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <Button variant="ghost" onClick={() => setStep(1)}>
                Quay lại
              </Button>
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                {create.isPending ? "Đang tạo…" : "Tạo website"}
              </Button>
            </div>
            {create.isError && (
              <p className="mt-3 text-sm text-destructive">{apiError(create.error).detail}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Per-theme miniature: mirrors the structural differences of the real themes
 * (hero size/colour + content grid) so the choice is made on layout, not name.
 * These previously lived in website-admin.css; inlined here after the big-bang
 * Tailwind migration dropped that stylesheet.
 */
/*
 * Bố cục riêng của bốn khoá CŨ. Tám chuyên khoa (#19458) dùng chung một bố cục nên rơi vào
 * `DEFAULT_PREVIEW` một cách có ý — chúng khác nhau ở MÀU, và màu đến từ `tokens` mà Sites công
 * bố (xem `previewColors`), không từ bảng này. Thêm tám dòng giống nhau vào đây là tám chỗ để
 * lệch khỏi theme thật.
 */
const THEME_PREVIEW: Record<string, PreviewSpec> = {
  premium: {
    hero: { height: 40, background: "#d9e2ef" },
    rowClass: "grid-cols-2",
  },
  diagnostics: {
    hero: { height: 22, background: "#e8eefc" },
    rowClass: "grid-cols-[2fr_1fr_1fr]",
  },
  dental: {
    hero: { height: 20, background: "#dbeafe", borderRadius: 8 },
    rowClass: "grid-cols-3",
    cellStyle: { borderRadius: 8, height: 26 },
  },
  aesthetic: {
    hero: { height: 44, background: "linear-gradient(180deg, #14181d, #2a3138)" },
    rowClass: "grid-cols-2",
  },
};

const DEFAULT_PREVIEW: PreviewSpec = {
  hero: { height: 30, background: "#f1f5f9" },
  rowClass: "grid-cols-3",
};

/**
 * Giao diện: chọn MỘT LẦN ở Bước 1, sửa ở Addy Sites (#18656, giữ nguyên ở #19459).
 *
 * <p>Không có ô chọn ở đây là có ý. Phòng khám chọn mẫu lúc TẠO website — lúc đó Sites chưa có
 * lựa chọn nào để bị đè — rồi từ đó về sau màu sắc, kiểu chữ, bố cục từng kênh đều sửa trong
 * Addy Sites, nơi xem trước được trực tiếp. HIS chỉ từng giữ MỘT `clinic_websites.theme_id`:
 * bản sao nghèo hơn của cùng một quyết định, và nó đã trôi khỏi bản thật (đo 15/09: HIS `dental`,
 * Sites `diagnostics` cho cùng một phòng khám).
 *
 * <p>Nếu sau này dựng lại ô chọn ở màn này thì phải dựng CẢ HAI nửa, không chỉ nửa ghi: ghi sang
 * Sites (`WebsiteService.changeTheme` đã làm từ #19459) VÀ đọc giá trị đang chạy từ Sites thay vì
 * in `theme_id` của HIS. Thiếu nửa đọc là chỗ lệch quay lại ngay, chỉ chậm hơn một nhịp.
 *
 * <p>Thứ HIS vẫn sở hữu là SLUG, ngay khối phía trên: danh tính phòng khám, dùng chung cho cả hai
 * kênh.
 */
function ThemeHandoff() {
  return (
    <div className="space-y-2">
      <h3 className="font-medium">Giao diện</h3>
      <p className="max-w-prose text-sm text-muted-foreground">
        Mẫu giao diện được chọn khi tạo website. Màu sắc, kiểu chữ và bố cục của website cũng như
        Zalo Mini App sửa trong Addy Sites — nơi xem trước được trực tiếp từng kênh.
      </p>
      <p className="max-w-prose text-sm text-muted-foreground">
        Địa chỉ công khai (slug) ở trên vẫn do đây quản lý, và dùng chung cho cả hai kênh.
      </p>
    </div>
  );
}

const SITE_SYNC_TEXT: Record<
  GatewaySiteSync,
  { label: string; tone: string; hint: string }
> = {
  SYNCED: {
    label: "Đã kết nối",
    tone: "text-emerald-700",
    hint: "Bệnh nhân đặt lịch trên website, lịch hẹn vào thẳng Addy HIS.",
  },
  NOT_SYNCED: {
    label: "Chưa đồng bộ sang website",
    tone: "text-amber-700",
    hint: "Website chưa nhận được khoá kết nối mới nhất nên đặt lịch trên website có thể không chạy. Bấm “Kết nối lại” để gửi lại.",
  },
  NOT_PROVISIONED: {
    label: "Website đang được khởi tạo",
    tone: "text-muted-foreground",
    hint: "Addy Sites chưa tạo xong website. Kết nối sẽ tự hoàn tất sau vài phút.",
  },
  UNKNOWN: {
    label: "Chưa kiểm tra được",
    tone: "text-muted-foreground",
    hint: "Không liên lạc được với Addy Sites lúc này. Thử tải lại trang sau ít phút.",
  },
};

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())} ${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Kết nối đặt lịch (#19003): whether the website's booking reaches HIS, plus "Kết nối lại" for a
 * technician when the key leaked or the sync failed. Rotating used to mean calling the API from
 * DevTools (#18999).
 *
 * The key is never shown — the POST returns the status, and the key goes from HIS to Addy Sites
 * server to server (#19001). The button needs `clinic.write`, the same permission the endpoint checks.
 */
function BookingConnection() {
  const qc = useQueryClient();
  const connection = useQuery({
    queryKey: ["website", "connection"],
    queryFn: websiteApi.connection,
  });
  const profile = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });
  const canReconnect = (profile.data?.permissions ?? []).includes(
    "clinic.write",
  );

  const reconnect = useMutation({
    mutationFn: websiteApi.connect,
    onSuccess: (data) => qc.setQueryData(["website", "connection"], data),
  });

  function onReconnect() {
    if (
      !window.confirm(
        "Cấp khoá kết nối mới cho website?\n\nKhoá cũ hết hiệu lực ngay. Đặt lịch trên website có thể gián đoạn trong vài phút.",
      )
    )
      return;
    reconnect.mutate();
  }

  const c = connection.data;
  const status = !c
    ? null
    : !c.connected
      ? {
          label: "Chưa kết nối",
          tone: "text-destructive",
          hint: "Website chưa có khoá kết nối nên chưa đặt lịch được. Bấm “Kết nối lại” để cấp khoá.",
        }
      : (SITE_SYNC_TEXT[c.siteSync] ?? SITE_SYNC_TEXT.UNKNOWN);
  const issuedAt = c ? formatDateTime(c.rotatedAt ?? c.createdAt) : null;

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-1">
        <h3 className="font-medium">Kết nối đặt lịch</h3>
        {connection.isLoading && <Skeleton className="h-4 w-56" />}
        {connection.isError && (
          <p className="text-sm text-destructive">
            Không tải được trạng thái kết nối:{" "}
            {apiError(connection.error).detail}
          </p>
        )}
        {status && (
          <>
            <p className={cn("text-sm font-medium", status.tone)}>
              ● {status.label}
            </p>
            <p className="max-w-prose text-sm text-muted-foreground">
              {status.hint}
            </p>
            {c?.connected && issuedAt && (
              <p className="text-xs text-muted-foreground">
                Khoá cấp lúc {issuedAt}
              </p>
            )}
          </>
        )}
        {reconnect.isError && (
          <p className="text-sm text-destructive">
            {apiError(reconnect.error).detail}
          </p>
        )}
      </div>
      {canReconnect && (
        <Button
          variant="secondary"
          onClick={onReconnect}
          disabled={reconnect.isPending || !c}
        >
          {reconnect.isPending ? "Đang kết nối…" : "Kết nối lại"}
        </Button>
      )}
    </div>
  );
}

/** Where the clinic fixes a missing OTP template (#19180) — the OTP row of the patient tab. */
const OTP_SETTINGS_LINK = "/settings/notifications#PATIENT_OTP";

function OtpChannelProblems({ channels }: { channels?: OtpChannelCheck[] }) {
  const problems = (channels ?? []).filter((c) => !c.ready && c.problem);
  if (problems.length === 0) return null;
  return (
    <ul className="list-disc pl-5 text-sm">
      {problems.map((c) => (
        <li key={c.channel}>{c.problem}</li>
      ))}
    </ul>
  );
}

function ChooseOtpTemplateButton() {
  return (
    <Button asChild size="sm" variant="outline" className="w-fit">
      <Link to={OTP_SETTINGS_LINK}>Chọn mẫu OTP</Link>
    </Button>
  );
}

/**
 * Tra cứu kết quả khám trên website (#19138, GĐ2.3-Q1 = c). HIS decides "allowed" — the data is
 * HIS's — and Addy Sites decides whether its page shows the lookup. Off by default: putting results
 * on the Internet is a choice the clinic makes here, knowingly. The status line under the switch is
 * exactly what the website's gateway reads, so "bật mà website vẫn không hiện" explains itself.
 */
export function ResultsLookupSetting() {
  const qc = useQueryClient();
  const lookup = useQuery({
    queryKey: ["website", "results-lookup"],
    queryFn: websiteApi.resultsLookup,
  });
  const profile = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });
  const canEdit = (profile.data?.permissions ?? []).includes("clinic.write");
  // No optimistic update (#19180): the switch changes only when the server says 200, because
  // turning it on is refused while no OTP template can reach a phone.
  const save = useMutation({
    mutationFn: websiteApi.setResultsLookup,
    onSuccess: (data) => qc.setQueryData(["website", "results-lookup"], data),
  });
  const saveError = save.isError ? apiError(save.error) : null;
  const refusedOtpChannels = (saveError?.problem?.otpChannels ?? undefined) as
    | OtpChannelCheck[]
    | undefined;

  const d = lookup.data;
  const reasonText = d ? resultsLookupReasonText(d) : null;
  // #19614 — what stops it being turned on, known BEFORE the click: the server answers the OTP half
  // whatever the switch says. Turning it OFF is never locked.
  const blockers = d ? turnOnBlockers(d) : [];
  const locked = !!d && !d.enabled && blockers.length > 0;
  // A click on the locked switch is answered, not swallowed: the reasons box is re-announced.
  const [nudged, setNudged] = useState(0);
  // #19191: bỏ tích là cắt luôn phiên đang mở, nên hỏi lại trước. Bật thì không cần hỏi.
  const onToggle = (next: boolean) => {
    if (!next && !window.confirm(`Tắt tra cứu kết quả trên website?

${RESULTS_LOOKUP_OFF_WARNING}`)) {
      return;
    }
    save.mutate(next);
  };
  return (
    <div className="space-y-2">
      <h3 className="font-medium">Tra cứu kết quả khám</h3>
      <p className="max-w-prose text-sm text-muted-foreground">
        Bệnh nhân nhập số điện thoại trên website, nhận mã xác thực rồi xem chẩn đoán, đơn thuốc và
        tệp kết quả phòng khám đã mở cho họ. Phiên xem tự đóng sau 15 phút.
      </p>
      {lookup.isLoading && <Skeleton className="h-4 w-56" />}
      {lookup.isError && (
        <p className="text-sm text-destructive">
          Không tải được cài đặt tra cứu kết quả: {apiError(lookup.error).detail}
        </p>
      )}
      {d && (
        <>
          <label
            className={cn(
              "flex w-fit items-center gap-2.5 rounded-md border border-input px-3.5 py-2.5 text-sm",
              canEdit && !locked ? "cursor-pointer hover:bg-muted" : "cursor-not-allowed opacity-70",
            )}
            title={locked ? "Chưa bật được: xem điều kiện bên dưới" : undefined}
            onClick={() => locked && setNudged((n) => n + 1)}
            data-testid="results-lookup-toggle"
          >
            <Checkbox
              checked={d.enabled}
              disabled={!canEdit || save.isPending || locked}
              aria-describedby={blockers.length > 0 ? "results-lookup-blockers" : undefined}
              onCheckedChange={(v) => onToggle(v === true)}
            />
            <span>Cho phép bệnh nhân tra cứu kết quả khám trên website</span>
          </label>
          {blockers.length > 0 && (
            <div
              id="results-lookup-blockers"
              key={nudged}
              role={nudged > 0 ? "alert" : "status"}
              data-testid="results-lookup-blockers"
              className={cn(
                "grid max-w-prose gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950/30 dark:text-amber-100",
                nudged > 0 && "ring-2 ring-amber-400",
              )}
            >
              <p className="font-medium">
                {d.enabled
                  ? "Website đang ẩn mục tra cứu vì mã xác thực chưa gửi được:"
                  : "Chưa bật được tra cứu kết quả — cần gửi được mã xác thực (OTP) trước:"}
              </p>
              <ul className="grid gap-2">
                {blockers.map((b) => (
                  <li key={b.key} className="grid gap-1">
                    <span>• {b.text}</span>
                    {b.details && b.details.length > 0 && (
                      <ul className="list-disc pl-8 text-xs">
                        {b.details.map((t) => (
                          <li key={t}>{t}</li>
                        ))}
                      </ul>
                    )}
                    {b.fix && (
                      <Button asChild size="sm" variant="outline" className="ml-3 w-fit">
                        <Link to={b.fix.to}>{b.fix.label}</Link>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ResultsLookupStatusBadge data={d} />
          {d.enabled && (
            <p className="max-w-prose text-xs text-muted-foreground">
              Bỏ tích là dừng ngay: bệnh nhân đang xem kết quả sẽ bị thoát ở thao tác kế tiếp.
            </p>
          )}
          {/* The website's gateway caches this answer for 60 s (addy-sites CAPABILITY_TTL_MS), so the
              form appears or disappears up to a minute after a change. Sessions and new codes are
              refused at once regardless — said so the clinic does not read the delay as a fault. */}
          <p className="max-w-prose text-xs text-muted-foreground" data-testid="results-lookup-delay">
            Trang tra cứu trên website cập nhật trong khoảng 1 phút sau khi đổi.
          </p>
          {/* The blockers box already says it, item by item. */}
          {reasonText && blockers.length === 0 && (
            <p className="max-w-prose text-sm text-muted-foreground">{reasonText}</p>
          )}
          {d.examResults.warning === "OTP_ZBS_ONLY" && (
            <p className="max-w-prose rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
              Chỉ gửi mã qua Zalo — bệnh nhân không dùng Zalo sẽ không nhận được mã. Thêm mẫu SMS
              để dự phòng.
            </p>
          )}
        </>
      )}
      {saveError?.code === "otp_not_ready" ? (
        <div
          role="alert"
          className="grid max-w-prose gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <p className="font-medium">{saveError.detail}</p>
          <OtpChannelProblems channels={refusedOtpChannels} />
          <ChooseOtpTemplateButton />
        </div>
      ) : saveError?.code === "otp_check_unavailable" ? (
        <p className="max-w-prose rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          ⚠️ {saveError.detail}
        </p>
      ) : (
        saveError && <p className="text-sm text-destructive">{saveError.detail}</p>
      )}
    </div>
  );
}

/** Skeleton-style mini mockup: a page-bar + a theme-coloured hero + a row of
 * content bars. Forced light chrome, independent of the admin dashboard's
 * dark/light mode — a theme preview's job is to show the real public-site
 * colours accurately, not whatever mode the admin happens to be in. */
/*
 * #19568 — màu lấy từ `tokens` mà addy-sites công bố cho TỪNG theme, không còn tô cứng. Trước đây
 * mọi ô nội dung là `bg-slate-800` và chỉ 4/10 theme có màu hero riêng, nên 6 theme còn lại hiện
 * y hệt nhau, toàn khối đen. Thiếu tokens (bản dự phòng khi Sites không trả lời) thì giữ màu cũ.
 */
function GenericPreview({ spec, tokens }: { spec: PreviewSpec; tokens?: AddyThemeTokens }) {
  // Màu là một quyết định và nó nằm ở `websitePreviewRules` — xem chú thích ở đó cho lý do.
  const colors = previewColors(spec, tokens);
  return (
    <span
      className="block h-[92px] overflow-hidden rounded-sm border border-slate-200 p-2"
      style={{ background: colors.background }}
      aria-hidden
    >
      <span className="mb-2 block h-1.5 rounded-sm" style={{ background: colors.ink }} />
      <span className="mb-2 block rounded-sm" style={colors.hero} />
      <span className={cn("grid gap-1.5", spec.rowClass)}>
        {colors.cells.map((cell, i) => (
          <span
            key={i}
            className="block h-[22px] rounded-sm border border-slate-200/70"
            style={{ background: cell, ...spec.cellStyle }}
          />
        ))}
      </span>
    </span>
  );
}

function ThemeCard({
  theme,
  selected,
  recommendedFor,
  onSelect,
}: {
  theme: AddyTheme;
  selected: boolean;
  /** Vertical label to justify the pre-selection, e.g. "Nha khoa". Empty on every other card. */
  recommendedFor: string;
  onSelect: () => void;
}) {
  const spec = THEME_PREVIEW[theme.key] ?? DEFAULT_PREVIEW;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        // Forced light chrome, independent of the admin dashboard's dark/light mode: a
        // theme preview's job is to show the real (light) public-site colours accurately —
        // reskinning it to the admin's current mode would misrepresent what it looks like.
        "flex flex-col gap-2 rounded-md border bg-white p-3 text-left shadow-sm transition-colors hover:border-primary/60",
        selected ? "border-primary ring-2 ring-primary/30" : "border-slate-200",
      )}
    >
      <GenericPreview spec={spec} tokens={theme.tokens} />
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-slate-900">{theme.name}</span>
        {recommendedFor ? (
          <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
            Hợp với {recommendedFor}
          </span>
        ) : null}
      </span>
      <span className="text-xs leading-relaxed text-slate-500">{theme.description}</span>
    </button>
  );
}

/**
 * The fixed prefix every clinic slug carries. The clinic only ever types the part after it, so a
 * saved slug is always `pk-<name>` — one consistent shape for addy-sites' host-based tenant routing.
 */

/**
 * Splits the public URL into the pieces either side of the editable part, so the field reads as the
 * real address instead of a bare slug box.
 *
 * <p>The domain tail is recovered from `publicUrl` rather than hardcoded, because it is the backend
 * (`ADDY_SITES_PUBLIC_URL_TEMPLATE`) that decides it and it differs per environment. When addy-sites
 * has no template configured `publicUrl` is null and there is no truthful tail to show — the field
 * then renders prefix + input alone rather than inventing a domain.
 */
function splitPublicUrl(website: Website): { scheme: string; domain: string | null } {
  const m = website.publicUrl ? /^(https?:\/\/)(.+)$/.exec(website.publicUrl) : null;
  if (!m) return { scheme: "http://", domain: null };
  const lead = `${website.slug}.`;
  return { scheme: m[1], domain: m[2].startsWith(lead) ? m[2].slice(lead.length) : null };
}

/** ADR-0040 "tùy chỉnh website" — pick a custom public slug with a live availability check. */
function SlugCustomizer({ website }: { website: Website }) {
  const qc = useQueryClient();
  const current = website.customSlug ?? "";
  // The field holds only what comes after `pk-`. A legacy slug saved without the prefix is shown
  // whole and picks the prefix up on its next save — slugs converge on one shape rather than two.
  const [value, setValue] = useState(
    current.startsWith(SLUG_PREFIX) ? current.slice(SLUG_PREFIX.length) : current
  );
  const [check, setCheck] = useState<{ valid: boolean; available: boolean } | null>(null);
  const [checking, setChecking] = useState(false);

  const { scheme, domain } = splitPublicUrl(website);
  // #19558: ví dụ trong ô là ĐỊA CHỈ CỦA CHÍNH phòng khám này, không phải slug thật của một phòng
  // khám khác như trước ("luongngoc"). Luật ở `websitePreviewRules`.
  const placeholder = slugPlaceholder(website.slug);
  const normalized = value.trim().toLowerCase();
  // Empty stays empty: that is "dùng mã phòng khám", not a slug of just the prefix.
  const fullSlug = normalized ? SLUG_PREFIX + normalized : "";

  const doCheck = async () => {
    if (!fullSlug) return;
    setChecking(true);
    try {
      const r = await websiteApi.slugCheck(fullSlug);
      setCheck({ valid: r.valid, available: r.available });
    } finally {
      setChecking(false);
    }
  };

  const save = useMutation({
    mutationFn: () => websiteApi.setSlug(fullSlug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website"] });
      setCheck(null);
    },
  });

  const changed = fullSlug !== current;
  const canSave = changed && (fullSlug === "" || (!!check?.valid && !!check?.available));

  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground">Đường dẫn công khai (slug)</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Chỉ nhập tên phòng khám — phần <span className="font-mono">{SLUG_PREFIX}</span> và tên miền
        là cố định. Chỉ gồm a-z, 0-9, gạch nối; không bắt đầu/kết thúc bằng gạch nối. Để trống = dùng
        mã phòng khám.
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <div className="ring-offset-background flex h-9 max-w-full items-center overflow-x-auto rounded-md border border-input bg-background px-3 font-mono text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <span className="select-none whitespace-nowrap text-muted-foreground">
            {scheme}
            {SLUG_PREFIX}
          </span>
          <input
            value={value}
            onChange={(e) => {
              // Sanitise as they type so the address preview can never show a slug the server
              // would reject.
              setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
              setCheck(null);
            }}
            placeholder={placeholder}
            size={Math.max(value.length, placeholder.length, 9)}
            aria-label="Tên phòng khám trong địa chỉ web"
            className="min-w-0 bg-transparent py-1 text-foreground outline-none placeholder:text-muted-foreground"
          />
          {domain && (
            <span className="select-none whitespace-nowrap text-muted-foreground">.{domain}</span>
          )}
        </div>
        <Button variant="secondary" onClick={doCheck} disabled={checking || !fullSlug}>
          {checking ? "Đang kiểm tra…" : "Kiểm tra"}
        </Button>
        <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
          {save.isPending ? "Đang lưu…" : "Lưu slug"}
        </Button>
      </div>
      {check && (
        <p
          className={cn(
            "mt-2 text-sm",
            check.valid && check.available ? "text-emerald-600" : "text-destructive"
          )}
        >
          {!check.valid
            ? "Slug không hợp lệ."
            : check.available
              ? "Slug khả dụng ✓"
              : "Slug đã có người dùng — chọn slug khác."}
        </p>
      )}
      {save.isError && (
        <p className="mt-2 text-sm text-destructive">{apiError(save.error).detail}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>Địa chỉ hiện tại:</span>
        {website.publicUrl ? (
          <>
            <a
              href={website.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-primary hover:underline"
            >
              {website.publicUrl}
            </a>
            <CopyUrlButton url={website.publicUrl} />
          </>
        ) : (
          <span className="font-mono">({website.slug})</span>
        )}
      </div>
    </div>
  );
}

/** Copy the public website address to the clipboard, with a brief "copied" confirmation. */
function CopyUrlButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={async () => {
        if (await copyText(url)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? "Đã sao chép ✓" : "Sao chép"}
    </Button>
  );
}
